import type {
  TimelineClip,
  TimelineProject,
  TimelineTextOverlay,
  TimelineTransition,
  TimelineAudioTrack,
  VolumeKeyframe,
  AspectRatio,
} from "./timeline";
import {
  ASPECT_RATIO_DIMENSIONS,
  DEFAULT_IMAGE_DURATION,
  MIN_CLIP_DURATION,
} from "./timeline";

/** Calculate the effective duration of a clip (accounting for trim + speed) */
export function getClipDuration(clip: TimelineClip): number {
  if (clip.type === "image") return clip.displayDuration;
  return (clip.trimEnd - clip.trimStart) / clip.speed;
}

/**
 * Effective overlap (in seconds) that the transition between clip i and
 * clip i+1 carves out of the timeline. ffmpeg's xfade shortens the
 * output by exactly this amount, so the timeline math has to as well
 * for WYSIWYG render fidelity.
 */
function transitionOverlap(
  project: TimelineProject,
  i: number,
): number {
  const t = project.transitions[i];
  if (!t || t.type === "cut" || t.duration <= 0) return 0;
  const overlap = Math.min(
    t.duration,
    getClipDuration(project.clips[i]),
    getClipDuration(project.clips[i + 1]),
  );
  if (project.clips[i]?.timelineStart != null || project.clips[i + 1]?.timelineStart != null) {
    const boundary = getClipStartTime(project, i) + getClipDuration(project.clips[i]);
    const nextStart = getClipStartTime(project, i + 1);
    if (nextStart > boundary + 0.001) return 0;
    if (nextStart >= boundary - 0.001) return overlap;
    return Math.min(overlap, boundary - nextStart);
  }
  return overlap;
}

/** Calculate total timeline duration, accounting for transition overlaps. */
export function getTotalDuration(project: TimelineProject): number {
  // Raw persisted drafts can omit `clips` entirely (older/partial schema),
  // so guard before reading `.length` — every other consumer does the same.
  if (!project.clips?.length) return 0;
  if (project.clips.some((clip) => clip.timelineStart != null)) {
    return Math.max(
      0,
      ...project.clips.map((clip, index) => {
        const start = getClipStartTime(project, index);
        const rawEnd = start + getClipDuration(clip);
        if (index === 0) return rawEnd;

        const incomingOverlap = transitionOverlap(project, index - 1);
        if (incomingOverlap <= 0) return rawEnd;

        const previousEnd =
          getClipStartTime(project, index - 1) + getClipDuration(project.clips[index - 1]);
        // Older explicit-position drafts may store the incoming clip at the
        // cut boundary even though ffmpeg starts it at boundary-overlap.
        // Account for that render shortening without double-subtracting
        // drafts that already encode the overlap in timelineStart.
        return Math.abs(start - previousEnd) <= 0.001 ? rawEnd - incomingOverlap : rawEnd;
      }),
    );
  }
  let total = 0;
  for (let i = 0; i < project.clips.length; i++) {
    total += getClipDuration(project.clips[i]);
    if (i < project.clips.length - 1) {
      total -= transitionOverlap(project, i);
    }
  }
  return Math.max(0, total);
}

/** Get the start time of a clip on the timeline */
export function getClipStartTime(
  project: TimelineProject,
  clipIndex: number,
): number {
  const explicitStart = project.clips[clipIndex]?.timelineStart;
  if (explicitStart != null) return explicitStart;
  let time = 0;
  for (let i = 0; i < clipIndex && i < project.clips.length; i++) {
    time += getClipDuration(project.clips[i]);
    time -= transitionOverlap(project, i);
  }
  return time;
}

/** Get start times for all clips in a single O(n) pass */
export function getClipStartTimes(project: TimelineProject): number[] {
  if (!project.clips?.length) return [];
  if (project.clips.some((clip) => clip.timelineStart != null)) {
    return project.clips.map((_clip, index) => getClipStartTime(project, index));
  }
  const starts: number[] = [];
  let time = 0;
  for (let i = 0; i < project.clips.length; i++) {
    starts.push(time);
    time += getClipDuration(project.clips[i]);
    time -= transitionOverlap(project, i);
  }
  return starts;
}

/** Get the end time of a clip on the timeline */
export function getClipEndTime(
  project: TimelineProject,
  clipIndex: number,
): number {
  return (
    getClipStartTime(project, clipIndex) +
    getClipDuration(project.clips[clipIndex])
  );
}

/**
 * Find which clip a given timeline time falls within. Returns
 * `{ clipIndex, localTime }`. When `time` is in a transition-overlap
 * region we return the *earlier* clip — that clip is the "live"
 * primary; the EditorPreview's secondary player handles compositing
 * the incoming clip during the window.
 */
export function timeToClip(
  project: TimelineProject,
  time: number,
): { clipIndex: number; localTime: number } | null {
  if (project.clips.some((clip) => clip.timelineStart != null)) {
    for (let i = 0; i < project.clips.length; i++) {
      const start = getClipStartTime(project, i);
      const duration = getClipDuration(project.clips[i]);
      if (time >= start && time < start + duration) {
        return { clipIndex: i, localTime: time - start };
      }
    }

    let lastIndex = -1;
    let lastEnd = -Infinity;
    for (let i = 0; i < project.clips.length; i++) {
      const end = getClipStartTime(project, i) + getClipDuration(project.clips[i]);
      if (end > lastEnd) {
        lastEnd = end;
        lastIndex = i;
      }
    }
    if (lastIndex >= 0 && Math.abs(time - lastEnd) < 0.001) {
      const lastDuration = getClipDuration(project.clips[lastIndex]);
      return { clipIndex: lastIndex, localTime: Math.max(0, lastDuration - 0.001) };
    }
    return null;
  }

  let elapsed = 0;
  for (let i = 0; i < project.clips.length; i++) {
    const duration = getClipDuration(project.clips[i]);
    if (time >= elapsed && time < elapsed + duration) {
      return { clipIndex: i, localTime: time - elapsed };
    }
    elapsed += duration;
    elapsed -= transitionOverlap(project, i);
  }
  // Snap to just before end of last clip at the exact boundary.
  if (project.clips.length > 0) {
    const lastIdx = project.clips.length - 1;
    const lastStart = getClipStartTime(project, lastIdx);
    const lastDuration = getClipDuration(project.clips[lastIdx]);
    if (Math.abs(time - (lastStart + lastDuration)) < 0.001) {
      return { clipIndex: lastIdx, localTime: Math.max(0, lastDuration - 0.001) };
    }
  }
  return null;
}

/** Convert a local time within a clip to the source media time (accounting for trim + speed) */
export function localTimeToSourceTime(
  clip: TimelineClip,
  localTime: number,
): number {
  if (clip.type === "image") return 0;
  return clip.trimStart + localTime * clip.speed;
}

/** Get text overlays active at a given timeline time */
export function getActiveOverlays(
  overlays: TimelineTextOverlay[],
  time: number,
): TimelineTextOverlay[] {
  return overlays.filter((o) => time >= o.startTime && time <= o.endTime);
}

/** Create a default TimelineProject from a single video source */
export function createProjectFromVideo(
  sourceUri: string,
  duration: number,
  width: number,
  height: number,
): TimelineProject {
  return createProjectFromSource({ sourceUri, type: "video", duration, width, height });
}

/** Create a default TimelineProject from an image */
export function createProjectFromImage(
  sourceUri: string,
  width: number,
  height: number,
): TimelineProject {
  return createProjectFromSource({ sourceUri, type: "image", duration: 0, width, height });
}

function createProjectFromSource(opts: {
  sourceUri: string;
  type: "video" | "image";
  duration: number;
  width: number;
  height: number;
}): TimelineProject {
  const { sourceUri, type, duration, width, height } = opts;
  const isVideo = type === "video";
  const detectedAspect = detectAspectRatio(width, height);
  const dims = ASPECT_RATIO_DIMENSIONS[detectedAspect];

  return {
    id: generateId(),
    version: 1,
    aspectRatio: detectedAspect,
    clips: [
      {
        id: generateId(),
        sourceUri,
        type,
        sourceDuration: isVideo ? duration : 0,
        sourceWidth: width,
        sourceHeight: height,
        timelineStart: 0,
        trimStart: 0,
        trimEnd: isVideo ? duration : 0,
        speed: 1,
        volume: isVideo ? 1 : 0,
        displayDuration: isVideo ? duration : DEFAULT_IMAGE_DURATION,
        filter: null,
      },
    ],
    transitions: [],
    textOverlays: [],
    audioTracks: [],
    outputWidth: dims.width,
    outputHeight: dims.height,
  };
}

/** Detect closest standard aspect ratio from pixel dimensions */
export function detectAspectRatio(
  width: number,
  height: number,
): AspectRatio {
  const ratio = width / height;
  const ratios: { ar: AspectRatio; value: number }[] = [
    { ar: "9:16", value: 9 / 16 },
    { ar: "4:5", value: 4 / 5 },
    { ar: "1:1", value: 1 },
    { ar: "16:9", value: 16 / 9 },
  ];
  let closest = ratios[0];
  let minDiff = Math.abs(ratio - closest.value);
  for (let i = 1; i < ratios.length; i++) {
    const diff = Math.abs(ratio - ratios[i].value);
    if (diff < minDiff) {
      minDiff = diff;
      closest = ratios[i];
    }
  }
  return closest.ar;
}

/** Split a clip at a local time offset, returning two new clips */
export function splitClipAtTime(
  clip: TimelineClip,
  localTime: number,
): [TimelineClip, TimelineClip] | null {
  if (clip.type === "image") return null; // Can't split images

  const sourceTime = localTimeToSourceTime(clip, localTime);
  // MIN_CLIP_DURATION is in wall-clock seconds; convert to source-time units
  const minSourceBuffer = MIN_CLIP_DURATION * clip.speed;
  if (
    sourceTime <= clip.trimStart + minSourceBuffer ||
    sourceTime >= clip.trimEnd - minSourceBuffer
  ) {
    return null; // Split point too close to edges
  }

  const left: TimelineClip = {
    ...clip,
    id: generateId(),
    trimEnd: sourceTime,
  };

  const leftDuration = getClipDuration(left);
  const right: TimelineClip = {
    ...clip,
    id: generateId(),
    timelineStart: clip.timelineStart != null ? clip.timelineStart + leftDuration : undefined,
    trimStart: sourceTime,
  };

  return [left, right];
}

/** Generate a short random ID */
export function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Get the active transition at a given timeline time.
 * Overlap model: transition window is [boundary - duration, boundary],
 * matching ffmpeg's xfade — the outgoing clip's last `duration` seconds
 * are blended with the incoming clip's first `duration` seconds, and
 * the boundary is where the outgoing clip ends and the incoming clip
 * becomes the sole live layer. progress = 0 at window start, 1 at
 * boundary.
 */
export function getActiveTransition(
  project: TimelineProject,
  time: number,
): {
  transitionIndex: number;
  transition: TimelineTransition;
  progress: number;
  outgoingClipIndex: number;
  incomingClipIndex: number;
} | null {
  if (!project.clips?.length) return null;
  for (let i = 0; i < project.transitions.length; i++) {
    const t = project.transitions[i];
    if (!t || t.type === "cut" || t.duration <= 0) continue;
    if (i >= project.clips.length - 1) continue;

    const effectiveDuration = transitionOverlap(project, i);
    if (effectiveDuration <= 0) continue;
    const boundaryTime = getClipEndTime(project, i);
    const windowStart = boundaryTime - effectiveDuration;

    if (time >= windowStart && time < boundaryTime) {
      const progress = clamp((time - windowStart) / effectiveDuration, 0, 1);
      return {
        transitionIndex: i,
        transition: { ...t, duration: effectiveDuration },
        progress,
        outgoingClipIndex: i,
        incomingClipIndex: i + 1,
      };
    }
  }
  return null;
}

/** Clamp a number between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Format seconds as M:SS or MM:SS */
export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds * 10) / 10);
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  const tenths = Math.round((s % 1) * 10);
  return `${mins}:${secs.toString().padStart(2, "0")}.${tenths}`;
}

/** Format seconds as M:SS (no tenths, for display) */
export function formatTimeShort(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Interpolate volume at a given time from a sorted array of keyframes.
 * `time` is relative to the track's timelineStart (same as keyframe.time).
 * Returns 0-1. If no keyframes, returns 1.
 * Linear interpolation between adjacent keyframes.
 */
export function interpolateVolume(
  keyframes: VolumeKeyframe[],
  time: number,
): number {
  if (keyframes.length === 0) return 1;
  if (keyframes.length === 1) return keyframes[0].value;

  // Before first keyframe: use first keyframe's value
  if (time <= keyframes[0].time) return keyframes[0].value;

  // After last keyframe: use last keyframe's value
  const last = keyframes[keyframes.length - 1];
  if (time >= last.time) return last.value;

  // Find surrounding keyframes and lerp
  for (let i = 0; i < keyframes.length - 1; i++) {
    const a = keyframes[i];
    const b = keyframes[i + 1];
    if (time >= a.time && time <= b.time) {
      const t = (time - a.time) / (b.time - a.time);
      return a.value + (b.value - a.value) * t;
    }
  }

  return last.value;
}

const ANIMATION_DURATION = 0.4; // seconds for entry/exit animations

/**
 * Compute the animation state for a text overlay at a given timeline time.
 * Returns { opacity, translateY, scale } modifiers (all default to identity: 1, 0, 1).
 */
export function getOverlayAnimationStyle(
  overlay: TimelineTextOverlay,
  timelineTime: number,
): { opacity: number; translateY: number; scale: number } {
  const result = { opacity: 1, translateY: 0, scale: 1 };
  const overlayDuration = overlay.endTime - overlay.startTime;
  const animDur = Math.min(ANIMATION_DURATION, overlayDuration / 3);

  // Entry animation
  const timeIntoOverlay = timelineTime - overlay.startTime;
  if (timeIntoOverlay < animDur && overlay.entryAnimation !== "none") {
    const p = timeIntoOverlay / animDur; // 0→1
    const eased = easeOutCubic(p);

    switch (overlay.entryAnimation) {
      case "fade":
        result.opacity = eased;
        break;
      case "slide-up":
        result.opacity = eased;
        result.translateY = (1 - eased) * 30;
        break;
      case "scale":
        result.opacity = eased;
        result.scale = 0.5 + eased * 0.5;
        break;
      case "typewriter":
        result.opacity = eased;
        break;
    }
  }

  // Exit animation
  const timeUntilEnd = overlay.endTime - timelineTime;
  if (timeUntilEnd < animDur && overlay.exitAnimation !== "none") {
    const rawP = timeUntilEnd / animDur; // 1→0 (p=1 at start of exit, p=0 at end)
    const eased = easeInCubic(1 - rawP); // 0→1 accelerating curve, then invert for opacity
    const easedOpacity = 1 - eased;

    switch (overlay.exitAnimation) {
      case "fade":
        result.opacity = Math.min(result.opacity, easedOpacity);
        break;
      case "slide-down":
        result.opacity = Math.min(result.opacity, easedOpacity);
        result.translateY = eased * 30;
        break;
      case "scale":
        result.opacity = Math.min(result.opacity, easedOpacity);
        result.scale = Math.min(result.scale, 1 - eased * 0.5);
        break;
    }
  }

  return result;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function easeInCubic(t: number): number {
  return t * t * t;
}

/** Get audio tracks active at a given timeline time */
export function getActiveAudioTracks(
  tracks: TimelineAudioTrack[],
  timelineTime: number,
): TimelineAudioTrack[] {
  return tracks.filter((track) => {
    const trackDuration = track.trimEnd - track.trimStart;
    const trackEnd = track.timelineStart + trackDuration;
    return timelineTime >= track.timelineStart && timelineTime < trackEnd;
  });
}
